import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Stethoscope,
  GraduationCap,
  Scissors,
  Home,
  Footprints,
  Building,
  Sun,
  Brain,
  Apple,
  Shield,
  Phone,
  User,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Star,
  MapPin,
  Mail,
  Globe,
  Clock,
  Share2,
  Heart,
  AlertCircle,
  CheckCircle,
  Users,
  Dog
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const providerIcons = {
  veterinarian: Stethoscope,
  specialist_vet: Heart,
  emergency_vet: AlertCircle,
  trainer: GraduationCap,
  groomer: Scissors,
  pet_sitter: Home,
  dog_walker: Footprints,
  boarding: Building,
  daycare: Sun,
  behaviorist: Brain,
  nutritionist: Apple,
  insurance: Shield,
  emergency_contact: Phone,
  other: User
};

const providerColors = {
  veterinarian: 'bg-red-100 text-red-600',
  specialist_vet: 'bg-pink-100 text-pink-600',
  emergency_vet: 'bg-orange-100 text-orange-600',
  trainer: 'bg-blue-100 text-blue-600',
  groomer: 'bg-purple-100 text-purple-600',
  pet_sitter: 'bg-green-100 text-green-600',
  dog_walker: 'bg-teal-100 text-teal-600',
  boarding: 'bg-indigo-100 text-indigo-600',
  daycare: 'bg-yellow-100 text-yellow-600',
  behaviorist: 'bg-cyan-100 text-cyan-600',
  nutritionist: 'bg-lime-100 text-lime-600',
  insurance: 'bg-slate-100 text-slate-600',
  emergency_contact: 'bg-rose-100 text-rose-600',
  other: 'bg-gray-100 text-gray-600'
};

const CareTeam = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [providers, setProviders] = useState([]);
  const [providerTypes, setProviderTypes] = useState([]);
  const [myPets, setMyPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState('all');

  const [form, setForm] = useState({
    provider_type: 'veterinarian',
    name: '',
    specialty: '',
    clinic_name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    emergency_phone: '',
    email: '',
    website: '',
    hours: '',
    notes: '',
    is_primary: false,
    policy_number: '',
    coverage_details: '',
    relationship: '',
    pet_ids: []
  });

  const [shareForm, setShareForm] = useState({
    shared_with_email: '',
    shared_with_name: '',
    include_notes: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [providersRes, typesRes, petsRes] = await Promise.all([
        axios.get(`${API}/care-team/providers`),
        axios.get(`${API}/care-team/provider-types`),
        axios.get(`${API}/pets/me`)
      ]);
      setProviders(providersRes.data);
      setProviderTypes(typesRes.data);
      setMyPets(petsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      provider_type: 'veterinarian',
      name: '',
      specialty: '',
      clinic_name: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      phone: '',
      emergency_phone: '',
      email: '',
      website: '',
      hours: '',
      notes: '',
      is_primary: false,
      policy_number: '',
      coverage_details: '',
      relationship: '',
      pet_ids: []
    });
    setSelectedProvider(null);
  };

  const handleCreate = async () => {
    if (!form.name) {
      toast.error('Please enter a name');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/care-team/providers`, form);
      toast.success('Provider added to your care team!');
      setAddDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add provider');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedProvider) return;

    setSaving(true);
    try {
      await axios.put(`${API}/care-team/providers/${selectedProvider.id}`, form);
      toast.success('Provider updated!');
      setEditDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to update provider');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (providerId, providerName) => {
    if (!window.confirm(`Remove ${providerName} from your care team?`)) return;

    try {
      await axios.delete(`${API}/care-team/providers/${providerId}`);
      toast.success('Provider removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove provider');
    }
  };

  const handleShare = async () => {
    if (!shareForm.shared_with_email) {
      toast.error('Please enter an email address');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/care-team/share`, shareForm);
      toast.success(`Care team shared with ${shareForm.shared_with_email}!`);
      setShareDialogOpen(false);
      setShareForm({ shared_with_email: '', shared_with_name: '', include_notes: false });
    } catch (error) {
      toast.error('Failed to share care team');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (provider) => {
    setSelectedProvider(provider);
    setForm({
      provider_type: provider.provider_type,
      name: provider.name,
      specialty: provider.specialty || '',
      clinic_name: provider.clinic_name || '',
      address: provider.address || '',
      city: provider.city || '',
      state: provider.state || '',
      zip_code: provider.zip_code || '',
      phone: provider.phone || '',
      emergency_phone: provider.emergency_phone || '',
      email: provider.email || '',
      website: provider.website || '',
      hours: provider.hours || '',
      notes: provider.notes || '',
      is_primary: provider.is_primary || false,
      policy_number: provider.policy_number || '',
      coverage_details: provider.coverage_details || '',
      relationship: provider.relationship || '',
      pet_ids: provider.pet_ids || []
    });
    setEditDialogOpen(true);
  };

  const filteredProviders = filterType === 'all' 
    ? providers 
    : providers.filter(p => p.provider_type === filterType);

  // Group providers by type for summary
  const providersByType = providers.reduce((acc, p) => {
    if (!acc[p.provider_type]) acc[p.provider_type] = [];
    acc[p.provider_type].push(p);
    return acc;
  }, {});

  const primaryVet = providers.find(p => p.provider_type === 'veterinarian' && p.is_primary);
  const emergencyVet = providers.find(p => p.provider_type === 'emergency_vet');

  const ProviderIcon = ({ type }) => {
    const Icon = providerIcons[type] || User;
    return <Icon className="w-5 h-5" />;
  };

  const renderForm = () => (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium mb-1 block">Provider Type</label>
          <Select
            value={form.provider_type}
            onValueChange={(val) => setForm({ ...form, provider_type: val })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {providerTypes.map((type) => (
                <SelectItem key={type.id} value={type.id}>
                  <div className="flex items-center gap-2">
                    <ProviderIcon type={type.id} />
                    {type.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Input
          placeholder="Name *"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </div>

      {form.provider_type === 'specialist_vet' && (
        <Input
          placeholder="Specialty (e.g., Cardiology, Dermatology)"
          value={form.specialty}
          onChange={(e) => setForm({ ...form, specialty: e.target.value })}
        />
      )}

      {form.provider_type === 'emergency_contact' && (
        <Input
          placeholder="Relationship (e.g., neighbor, family, friend)"
          value={form.relationship}
          onChange={(e) => setForm({ ...form, relationship: e.target.value })}
        />
      )}

      <Input
        placeholder="Clinic/Business Name"
        value={form.clinic_name}
        onChange={(e) => setForm({ ...form, clinic_name: e.target.value })}
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          placeholder="Phone"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
        <Input
          placeholder="Emergency Phone"
          value={form.emergency_phone}
          onChange={(e) => setForm({ ...form, emergency_phone: e.target.value })}
        />
      </div>

      <Input
        placeholder="Email"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
      />

      <Input
        placeholder="Website"
        value={form.website}
        onChange={(e) => setForm({ ...form, website: e.target.value })}
      />

      <Input
        placeholder="Address"
        value={form.address}
        onChange={(e) => setForm({ ...form, address: e.target.value })}
      />

      <div className="grid grid-cols-3 gap-4">
        <Input
          placeholder="City"
          value={form.city}
          onChange={(e) => setForm({ ...form, city: e.target.value })}
        />
        <Input
          placeholder="State"
          value={form.state}
          onChange={(e) => setForm({ ...form, state: e.target.value })}
        />
        <Input
          placeholder="ZIP"
          value={form.zip_code}
          onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
        />
      </div>

      <Input
        placeholder="Hours (e.g., Mon-Fri 9am-5pm)"
        value={form.hours}
        onChange={(e) => setForm({ ...form, hours: e.target.value })}
      />

      {form.provider_type === 'insurance' && (
        <>
          <Input
            placeholder="Policy Number"
            value={form.policy_number}
            onChange={(e) => setForm({ ...form, policy_number: e.target.value })}
          />
          <Textarea
            placeholder="Coverage Details"
            value={form.coverage_details}
            onChange={(e) => setForm({ ...form, coverage_details: e.target.value })}
            rows={2}
          />
        </>
      )}

      <Textarea
        placeholder="Notes"
        value={form.notes}
        onChange={(e) => setForm({ ...form, notes: e.target.value })}
        rows={2}
      />

      {myPets.length > 0 && (
        <div>
          <label className="text-sm font-medium mb-2 block">Assign to Pets (optional)</label>
          <div className="flex flex-wrap gap-2">
            {myPets.map((pet) => (
              <label key={pet.id} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.pet_ids.includes(pet.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setForm({ ...form, pet_ids: [...form.pet_ids, pet.id] });
                    } else {
                      setForm({ ...form, pet_ids: form.pet_ids.filter(id => id !== pet.id) });
                    }
                  }}
                  className="rounded"
                />
                <span className="text-sm">{pet.name}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Leave empty to make available for all pets
          </p>
        </div>
      )}

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={form.is_primary}
          onChange={(e) => setForm({ ...form, is_primary: e.target.checked })}
          className="rounded"
        />
        <span className="text-sm font-medium">Primary provider for this type</span>
      </label>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-medium flex items-center gap-3">
              <Users className="w-8 h-8 text-primary" />
              My Pet Care Team
            </h1>
            <p className="text-muted-foreground">
              Keep track of your veterinarians, trainers, groomers, and other pet care providers
            </p>
          </div>
          <div className="flex gap-3">
            <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" disabled={providers.length === 0}>
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading">Share Care Team</DialogTitle>
                  <DialogDescription>
                    Share your pet care team with family or friends for emergencies
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <Input
                    placeholder="Email address *"
                    value={shareForm.shared_with_email}
                    onChange={(e) => setShareForm({ ...shareForm, shared_with_email: e.target.value })}
                  />
                  <Input
                    placeholder="Their name (optional)"
                    value={shareForm.shared_with_name}
                    onChange={(e) => setShareForm({ ...shareForm, shared_with_name: e.target.value })}
                  />
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={shareForm.include_notes}
                      onChange={(e) => setShareForm({ ...shareForm, include_notes: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Include personal notes</span>
                  </label>
                  <Button onClick={handleShare} className="w-full btn-primary" disabled={saving}>
                    {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Share Care Team
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={addDialogOpen} onOpenChange={(open) => {
              setAddDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-provider-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Provider
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading">Add Care Provider</DialogTitle>
                </DialogHeader>
                {renderForm()}
                <Button onClick={handleCreate} className="w-full btn-primary mt-4" disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Add to Care Team
                </Button>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Quick Stats */}
        {providers.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className={primaryVet ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50'}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${primaryVet ? 'bg-green-100' : 'bg-orange-100'}`}>
                  <Stethoscope className={`w-5 h-5 ${primaryVet ? 'text-green-600' : 'text-orange-600'}`} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Primary Vet</p>
                  <p className="font-medium truncate">
                    {primaryVet ? primaryVet.name : 'Not set'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className={emergencyVet ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50'}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${emergencyVet ? 'bg-green-100' : 'bg-orange-100'}`}>
                  <AlertCircle className={`w-5 h-5 ${emergencyVet ? 'text-green-600' : 'text-orange-600'}`} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Emergency Vet</p>
                  <p className="font-medium truncate">
                    {emergencyVet ? emergencyVet.name : 'Not set'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Providers</p>
                  <p className="font-medium">{providers.length}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Dog className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">My Pets</p>
                  <p className="font-medium">{myPets.length}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter */}
        {providers.length > 0 && (
          <div className="mb-6">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Providers</SelectItem>
                {providerTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name} ({providersByType[type.id]?.length || 0})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Providers List */}
        {providers.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Users className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="font-heading text-xl mb-2">No care providers yet</h3>
              <p className="text-muted-foreground mb-4">
                Add your veterinarians, trainers, groomers, and other pet care providers
              </p>
              <Button onClick={() => setAddDialogOpen(true)} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Provider
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProviders.map((provider) => {
              const Icon = providerIcons[provider.provider_type] || User;
              const colorClass = providerColors[provider.provider_type] || 'bg-gray-100 text-gray-600';
              const typeInfo = providerTypes.find(t => t.id === provider.provider_type);

              return (
                <Card key={provider.id} className="card-interactive" data-testid={`provider-${provider.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${colorClass}`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-heading font-medium">{provider.name}</h3>
                            {provider.is_primary && (
                              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                            )}
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {typeInfo?.name || provider.provider_type}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(provider)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(provider.id, provider.name)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {provider.specialty && (
                      <p className="text-sm text-muted-foreground mb-2">
                        Specialty: {provider.specialty}
                      </p>
                    )}

                    {provider.clinic_name && (
                      <p className="text-sm flex items-center gap-2 mb-1">
                        <Building className="w-4 h-4 text-muted-foreground" />
                        {provider.clinic_name}
                      </p>
                    )}

                    {provider.phone && (
                      <a href={`tel:${provider.phone}`} className="text-sm flex items-center gap-2 mb-1 hover:text-primary">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        {provider.phone}
                      </a>
                    )}

                    {provider.emergency_phone && (
                      <a href={`tel:${provider.emergency_phone}`} className="text-sm flex items-center gap-2 mb-1 text-red-600 hover:text-red-700">
                        <AlertCircle className="w-4 h-4" />
                        Emergency: {provider.emergency_phone}
                      </a>
                    )}

                    {(provider.city || provider.state) && (
                      <p className="text-sm flex items-center gap-2 mb-1 text-muted-foreground">
                        <MapPin className="w-4 h-4" />
                        {provider.city}{provider.state && `, ${provider.state}`}
                      </p>
                    )}

                    {provider.hours && (
                      <p className="text-sm flex items-center gap-2 mb-1 text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        {provider.hours}
                      </p>
                    )}

                    {provider.pet_names && provider.pet_names.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-muted-foreground">
                          Assigned to: {provider.pet_names.join(', ')}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        setEditDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit {selectedProvider?.name}</DialogTitle>
          </DialogHeader>
          {renderForm()}
          <Button onClick={handleUpdate} className="w-full btn-primary mt-4" disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Update Provider
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CareTeam;
